// Hook for HYK-792: Modal for prompting users to add social networks
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

// Kill-switch: disable HYK-792 social networks prompt everywhere.
const DISABLE_SOCIAL_NETWORKS_PROMPT = true;

/**
 * Check if user has any social network links
 * @param {Object} user - User object
 * @returns {boolean} - True if user has at least one social link
 */
export const hasSocialLinks = (user) => {
  if (!user) return false;
  
  // Check each social link - must be truthy and non-empty string (after trimming)
  const instagram = user.instagram?.trim();
  const twitter = user.twitter?.trim();
  const facebook = user.facebook?.trim();
  const youtube = user.youtube?.trim();
  
  return !!(instagram || twitter || facebook || youtube);
};

/**
 * Get storage key for a trigger with today's date
 * @param {string} trigger - Trigger name (app-open, invite, share, etc.)
 * @returns {string} - Storage key
 */
const getStorageKey = (trigger) => {
  const today = new Date().toISOString().split('T')[0];
  return `social-prompt-${trigger}-${today}`;
};

/**
 * Check if modal was shown today for a trigger
 * @param {string} trigger - Trigger name
 * @returns {boolean} - True if shown today
 */
export const wasShownToday = (trigger) => {
  try {
    const key = getStorageKey(trigger);
    return localStorage.getItem(key) === 'true';
  } catch (error) {
    console.error('Error checking if shown today:', error);
    return false;
  }
};

/**
 * Mark modal as shown today for a trigger
 * @param {string} trigger - Trigger name
 */
export const markShownToday = (trigger) => {
  try {
    const key = getStorageKey(trigger);
    localStorage.setItem(key, 'true');
  } catch (error) {
    console.error('Error marking as shown today:', error);
  }
};

/**
 * Determine if modal should be shown based on trigger, user, and creator status
 * @param {string} trigger - Trigger name (app-open, invite, share, creation-publish, news-publish)
 * @param {Object} user - User object
 * @param {boolean} isCreator - Whether user is a creator
 * @returns {boolean} - True if modal should be shown
 */
export const shouldShowModal = (trigger, user, isCreator = false) => {
  if (DISABLE_SOCIAL_NETWORKS_PROMPT) {
    return false;
  }
  // Don't show if user has social links
  if (hasSocialLinks(user)) {
    return false;
  }

  // Creator-only triggers
  if (trigger === 'creation-publish' || trigger === 'news-publish') {
    return isCreator;
  }

  // Triggers with "once per day" limit
  if (trigger === 'app-open' || trigger === 'invite') {
    return !wasShownToday(trigger);
  }

  // Share trigger - immediate (no frequency limit)
  if (trigger === 'share') {
    return true;
  }

  return false;
};

/**
 * Hook to manage social networks prompt modal
 * @param {string} trigger - Trigger name
 * @returns {Object} - { isOpen, openModal, closeModal, shouldShow }
 */
const useSocialNetworksPrompt = (trigger) => {
  const user = useSelector((state) => state.auth?.user);
  const [isOpen, setIsOpen] = useState(false);

  // Check if user is creator
  const isCreator = user?.role === 'creator';

  // Determine if modal should be shown
  const shouldShow = shouldShowModal(trigger, user, isCreator);

  // Auto-open modal if conditions are met
  // Note: 'share', 'creation-publish', and 'news-publish' triggers should NOT auto-open
  // They should only open when explicitly called via openModal()
  useEffect(() => {
    // Only auto-open for triggers that should show automatically (app-open, invite)
    // Share, creation-publish, and news-publish triggers are manual only
    const shouldAutoOpen = (trigger === 'app-open' || trigger === 'invite');
    
    if (shouldAutoOpen && shouldShow && !isOpen && user?.user_id) {
      setIsOpen(true);
      // Mark as shown for triggers with "once per day" limit
      markShownToday(trigger);
    }
  }, [shouldShow, trigger, isOpen, user?.user_id]);

  const openModal = useCallback(() => {
    // Only open if user object is loaded and conditions are met
    if (user?.user_id && shouldShowModal(trigger, user, isCreator)) {
      setIsOpen(true);
      // Mark as shown for triggers with "once per day" limit
      if (trigger === 'app-open' || trigger === 'invite') {
        markShownToday(trigger);
      }
    }
  }, [trigger, user, isCreator]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    openModal,
    closeModal,
    shouldShow,
  };
};

export default useSocialNetworksPrompt;

